import { PrimaryLanguage, TemplateComplexity } from "../../types";

// Diagram registry - pre-built diagrams for common templates
export interface DiagramConfig {
  diagram: string;
  style: "k8s" | "cloud-native" | "simple";
  optimizedFor: string[];
  description: string;
}

export const TEMPLATE_DIAGRAMS: Record<string, DiagramConfig[]> = {
  "react-fastapi-postgres": [
    {
      style: "k8s",
      optimizedFor: [
        "rusha_hosted",
        "aws_eks",
        "gcp_gke",
        "azure_aks",
        "k8s_self_hosted",
      ],
      description:
        "Kubernetes deployment with Ingress, Services, and StatefulSets",
      diagram: `flowchart TB
        subgraph Internet["Internet"]
          Client["Client Browser"]
        end
        
        subgraph K8s["Kubernetes Cluster"]
          IG["Ingress Controller"]
          
          subgraph AppServices["Application Services"]
            UISvc["Service: ui:80"]
            APISvc["Service: api:8000"]
          end
          
          subgraph DataLayer["Data Layer"]
            RedisSvc["Service: redis:6379"]
            PGSvc["Service: db:5432"]
          end
        end
        
        Client -->|HTTPS| IG
        IG -->|/| UISvc
        IG -->|/api| APISvc
        APISvc -->|cache| RedisSvc
        APISvc -->|SQL| PGSvc
        
        style Internet fill:#f9fafb,stroke:#374151
        style Client fill:#374151,stroke:#374151,color:#fff
        style K8s fill:#dbeafe,stroke:#2563eb
        style AppServices fill:#dcfce7,stroke:#16a34a
        style DataLayer fill:#fef3c7,stroke:#d97706
        style IG fill:#0066ff,stroke:#0066ff,color:#fff`,
    },
    {
      style: "cloud-native",
      optimizedFor: ["aws_services", "gcp_services", "azure_services"],
      description: "Cloud-native deployment with managed services",
      diagram: `flowchart TB
        subgraph Internet["Internet"]
          Client["Client Browser"]
        end
        
        subgraph Cloud["Cloud Provider"]
          CDN["CDN"]
          FE["Static Site Hosting"]
          API["API Service"]
          Cache["Cache"]
          DB["Database"]
        end
        
        Client --> CDN
        CDN --> FE
        Client -->|/api| API
        API --> Cache
        API --> DB
        
        style Internet fill:#f9fafb,stroke:#374151
        style Client fill:#374151,stroke:#374151,color:#fff
        style Cloud fill:#dbeafe,stroke:#2563eb`,
    },
  ],

  "nextjs-nestjs-postgres": [
    {
      style: "k8s",
      optimizedFor: [
        "rusha_hosted",
        "aws_eks",
        "gcp_gke",
        "azure_aks",
        "k8s_self_hosted",
      ],
      description:
        "Kubernetes deployment with Next.js frontend and NestJS backend",
      diagram: `flowchart TB
        subgraph Internet["Internet"]
          Client["Client Browser"]
        end
        
        subgraph K8s["Kubernetes Cluster"]
          IG["Ingress Controller"]
          
          subgraph Services["Services"]
            Next["Next.js:3000"]
            NestAPI["NestJS:8000"]
            Redis["Redis:6379"]
            PG["PostgreSQL:5432"]
          end
        end
        
        Client --> IG
        IG -->|/app| Next
        IG -->|/api| NestAPI
        NestAPI --> Redis
        NestAPI --> PG
        
        style Client fill:#374151,stroke:#374151,color:#fff
        style IG fill:#0066ff,stroke:#0066ff,color:#fff
        style Next fill:#000000,stroke:#000000,color:#fff
        style NestAPI fill:#ea2454,stroke:#ea2454,color:#fff
        style Redis fill:#d97706,stroke:#d97706
        style PG fill:#336791,stroke:#336791,color:#fff`,
    },
  ],

  "modern-lakehouse": [
    {
      style: "k8s",
      optimizedFor: [
        "rusha_hosted",
        "aws_eks",
        "gcp_gke",
        "azure_aks",
        "k8s_self_hosted",
      ],
      description: "Modern data lakehouse with Spark, Iceberg, and Trino",
      diagram: `flowchart TB
        subgraph Sources["Data Sources"]
          S3[S3/GCS]
          Kafka[Kafka]
          DB[(Database)]
        end
        
        subgraph Lakehouse["Lakehouse Cluster"]
          Spark[Spark Processing]
          Iceberg[Iceberg Tables]
          Trino[Trino Query Engine]
        end
        
        subgraph Storage["Storage"]
          Bronze[Bronze Layer]
          Silver[Silver Layer]
          Gold[Gold Layer]
        end
        
        S3 --> Spark
        Kafka --> Spark
        DB --> Spark
        Spark --> Bronze
        Bronze --> Silver
        Silver --> Gold
        Trino --> Iceberg
        Iceberg --> Bronze
        
        style S3 fill:#ff9900,stroke:#ff9900
        style Kafka fill:#000000,stroke:#000000,color:#fff
        style DB fill:#336791,stroke:#336791,color:#fff
        style Spark fill:#e24554,stroke:#e24554,color:#fff
        style Iceberg fill:#d97706,stroke:#d97706
        style Trino fill:#0066ff,stroke:#0066ff,color:#fff`,
    },
  ],

  default: [
    {
      style: "simple",
      optimizedFor: [
        "rusha_hosted",
        "aws_eks",
        "gcp_gke",
        "azure_aks",
        "k8s_self_hosted",
      ],
      description: "Generic Kubernetes deployment",
      diagram: `flowchart TB
        Client["Client"] --> LB["Load Balancer"]
        LB --> App["Application Service"]
        App --> Cache["Cache"]
        App --> DB["Database"]
        
        style Client fill:#374151,stroke:#374151,color:#fff
        style LB fill:#0066ff,stroke:#0066ff,color:#fff
        style App fill:#10b981,stroke:#10b981
        style Cache fill:#f59e0b,stroke:#f59e0b
        style DB fill:#7c3aed,stroke:#7c3aed`,
    },
  ],
};

export const COMPLEXITY_COLOR: Record<
  TemplateComplexity,
  "success" | "warning" | "error"
> = {
  low: "success",
  medium: "warning",
  high: "error",
};

export const COMPLEXITY_LABEL: Record<TemplateComplexity, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

export const TOOL_LABELS: Record<string, string> = {
  airbyte: "Airbyte",
  debezium_kafka: "Debezium + Kafka",
  kafka: "Kafka",
  meltano: "Meltano",
  dbt: "dbt",
  spark: "Spark",
  flink: "Flink",
  dbt_duckdb: "dbt + DuckDB",
  iceberg: "Iceberg",
  delta_lake: "Delta Lake",
  hudi: "Hudi",
  hive_metastore: "Hive Metastore",
  polaris: "Polaris",
  nessie: "Nessie",
  trino: "Trino",
  spark_sql: "Spark SQL",
  duckdb: "DuckDB",
  postgres: "PostgreSQL",
  dagster: "Dagster",
  airflow: "Airflow",
  prefect: "Prefect",
  superset: "Superset",
  metabase: "Metabase",
  grafana: "Grafana",
  beaver: "Beaver",
  clickhouse: "ClickHouse",
  starrocks: "StarRocks",
  druid: "Druid",
  pinot: "Pinot",
  redash: "Redash",
  iceberg_catalog: "Iceberg Catalog",
  olap_load: "OLAP Load",
  kafka_stream: "Kafka Stream",
  relational_db: "Relational DB",
  // Frontend frameworks
  react: "React",
  nextjs: "Next.js",
  vue: "Vue",
  angular: "Angular",
  svelte: "Svelte",
  remix: "Remix",
  // Python backends
  fastapi: "FastAPI",
  django: "Django",
  flask: "Flask",
  litestar: "Litestar",
  // TypeScript/Node backends
  nestjs: "NestJS",
  express: "Express",
  fastify: "Fastify",
  hono: "Hono",
  // Java backends
  spring_boot: "Spring Boot",
  quarkus: "Quarkus",
  micronaut: "Micronaut",
  // Go backends
  go_fiber: "Go Fiber",
  gin: "Gin",
  echo: "Echo",
  chi: "Chi",
  // Databases
  postgresql: "PostgreSQL",
  mysql: "MySQL",
  mongodb: "MongoDB",
  sqlite: "SQLite",
  bigquery: "BigQuery",
  // Cache / queues
  redis: "Redis",
  memcached: "Memcached",
  rabbitmq: "RabbitMQ",
  // AWS managed services
  kinesis: "Amazon Kinesis",
  msk: "Amazon MSK",
  aws_glue_crawler: "AWS Glue Crawler",
  dms: "AWS DMS",
  aws_glue_etl: "AWS Glue ETL",
  emr_spark: "Amazon EMR (Spark)",
  emr_flink: "Amazon EMR (Flink)",
  aws_glue_catalog: "AWS Glue Catalog",
  athena: "Amazon Athena",
  redshift: "Amazon Redshift",
  mwaa: "Amazon MWAA",
  quicksight: "Amazon QuickSight",
  aurora_postgres: "Aurora PostgreSQL",
};

export const LANGUAGE_LABEL: Record<PrimaryLanguage, string> = {
  java: "Java",
  python: "Python",
  typescript: "TypeScript",
  go: "Go",
  rust: "Rust",
  multi: "Multi-language",
};
